import { connect } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { FontIcon } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import SettlementDebtsInfoComponent from "./SettlementDebtsInfo.Component";
import * as ComponentFunctions from './SettlementDebtsDetails.Functions';
import { debtsTone, summarizeDebts } from './SettlementDebtsDetails.Summary';
import SettlementChangeStatusConfirmationFormComponent from "../SettlementChangeStatusConfirmationForm/SettlementChangeStatusConfirmation.Form.Component";
import { SettlementStatusEnum } from "../../../models/Enums/SettlementStatusEnum";
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import PaymentFormComponent from '../../CashFlow/Payments/Payment.Form/Payment.Form.Component';
import { FormType as PaymentFormType } from '../../CashFlow/Payments/Payment.Form/Payment.Form.Function';
import { SettlementDebt } from '../../../models/Entities/SettlementDebt/SettlementDebt';
import './SettlementDebtsDetails.Styles.css';

/** Cada cuánto se vuelve a preguntar por las deudas de una liquidación confirmada que todavía no las tiene. */
const POLL_MS = 5000;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const SettlementDebtsDetailsComponent = (props: ComponentFunctions.Props) => {
    const pollRef = useRef<number | null>(null);
    // Deuda que se está cobrando (o pagando) desde acá; abre el formulario de caja precargado.
    const [settling, setSettling] = useState<SettlementDebt | null>(null);
    // El centro cobra si es el acreedor de la deuda; si no, paga.
    const settleType = settling?.CashFlowDebt?.creditor === props.OwnerId ? PaymentFormType.receiver : PaymentFormType.payer;
    const centerCollects = (props.Debts ?? []).some((debt) => debt.CashFlowDebt?.creditor === props.OwnerId);

    const summary = summarizeDebts(props.Debts);
    // Confirmada o con deuda impaga: en los dos casos tiene que haber deudas generadas.
    const isConfirmed = props.settlement?.status === SettlementStatusEnum.CONFIRMED
        || props.settlement?.status === SettlementStatusEnum.DEBT_UNPAID;
    const tone = debtsTone({ summary, hasLoaded: props.hasLoaded, settlementConfirmed: isConfirmed });
    const isBusy = props.isLoading || props.isChangingSettlementStatus === true;

    // El vínculo deuda-liquidación y el detalle de cashflow son dos pedidos:
    // se hacen juntos, así la lista aparece una sola vez y completa.
    const refresh = useCallback(() => {
        props.GetSettlementDebtsBySettlementId(props.SettlementId);
        props.GetDebtsBySettlementId(props.SettlementId, undefined, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.SettlementId]);

    useEffect(() => {
        props.ResetSettlementChangeStatusFlags(props.SettlementId);
        refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.SettlementId]);

    // Un vínculo cuyo detalle de cashflow todavía no existe se reintenta con
    // espera: si el detalle llega mientras tanto, el reintento se cancela solo.
    useEffect(() => {
        const incomplete = summary.total > 0 && summary.enriched < summary.total;
        if (!incomplete || props.isLoading) return;

        const timer = window.setTimeout(
            () => props.GetDebtsBySettlementId(props.SettlementId, undefined, false),
            POLL_MS
        );
        return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summary.total, summary.enriched, props.isLoading]);

    // Confirmada y sin deudas: el sistema las genera de fondo, se espera.
    useEffect(() => {
        const shouldPoll = isConfirmed && props.hasLoaded && summary.total === 0;

        if (shouldPoll && pollRef.current === null) {
            pollRef.current = window.setInterval(refresh, POLL_MS);
        }
        if (!shouldPoll && pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }

        return () => {
            if (pollRef.current !== null) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [isConfirmed, props.hasLoaded, summary.total, refresh]);

    useEffect(() => {
        if (props.isChangingSettlementStatusSuccessfully) {
            props.GetSettlementById(props.SettlementId);
            refresh();
            props.ResetSettlementChangeStatusFlags(props.SettlementId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isChangingSettlementStatusSuccessfully]);

    const summaryText = summary.total > 0
        ? [
            plural(summary.total, 'deuda', 'deudas'),
            summary.paid > 0 ? `${summary.paid} ${summary.paid === 1 ? 'pagada' : 'pagadas'}` : '',
            summary.pending > 0 ? `${summary.pending} ${summary.pending === 1 ? 'pendiente' : 'pendientes'}` : ''
        ].filter(Boolean).join(' · ')
        : '';

    return (
        <div className={`sdd-root sdd-root--${tone}`}>

            {/* ── Resumen + acción ─────────────────────────────────────── */}
            <div className="sdd-bar">
                <span className={`sdd-dot sdd-dot--${tone}`} aria-hidden />
                <div className="sdd-bar__text">
                    {summary.total > 0 ? (
                        <>
                            <span className="sdd-bar__summary">{summaryText}</span>
                            {summary.pendingByCurrency.length > 0 && (
                                <span className="sdd-bar__pending">
                                    Saldo pendiente:{' '}
                                    {summary.pendingByCurrency.map((item) => (
                                        <strong key={item.currency}>{formatCurrencyAmountWithIssue(item.amount, item.currency)}</strong>
                                    ))}
                                </span>
                            )}
                            {summary.pending === 0 && summary.enriched === summary.total && (
                                <span className="sdd-bar__ok">
                                    <FontIcon iconName="SkypeCircleCheck" /> Todo cobrado
                                </span>
                            )}
                        </>
                    ) : tone === 'loading' ? (
                        <span className="sdd-bar__summary sdd-bar__summary--muted">Cargando deudas…</span>
                    ) : tone === 'waiting' ? (
                        <span className="sdd-bar__summary sdd-bar__summary--muted">
                            Las deudas todavía no se generaron. Se vuelve a verificar cada {POLL_MS / 1000} segundos.
                        </span>
                    ) : (
                        <span className="sdd-bar__summary sdd-bar__summary--muted">Sin deudas asociadas</span>
                    )}
                    {props.failOnLoading && (
                        <span className="sdd-bar__error">
                            <FontIcon iconName="Warning" /> No se pudieron cargar las deudas.
                        </span>
                    )}
                </div>
                <div className="sdd-bar__actions">
                    {isBusy && <Spinner size="extra-tiny" aria-label="Actualizando" />}
                    <SettlementChangeStatusConfirmationFormComponent
                        SettlementId={props.SettlementId}
                        NextStatusIsActionConfirmed={SettlementStatusEnum.PAID}
                        disabled={isBusy}
                    />
                </div>
            </div>

            {/* ── Lista ────────────────────────────────────────────────── */}
            {summary.total > 0 && (
                <SettlementDebtsInfoComponent
                    SettlementDebts={props.Debts ?? []}
                    onSettle={(debt) => debt.CashFlowDebt && setSettling(debt)}
                    settleLabel={centerCollects ? 'Registrar cobro' : 'Registrar pago'}
                />
            )}

            <PaymentFormComponent
                FormType={settleType}
                ReferalId={props.OwnerId}
                FixedAssociatedEntityIdentifier={undefined}
                PrefillDebtsData={settling?.CashFlowDebt
                    // La deuda de caja no trae protocolo: se toma el de la liquidación.
                    ? [{ ...settling.CashFlowDebt, protocolId: settling.CashFlowDebt.protocolId ?? props.settlement?.protocol?.[0]?.protocolId ?? null }]
                    : undefined}
                isOpen={settling !== null}
                onClose={() => { setSettling(null); refresh(); }}
            />
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementDebtsDetailsComponent as any);
